import { forwardFromYaw, type TileLayer } from '@etown/shared';
import { distanceToRect, rayRectDistance } from './aiming';

/**
 * Sich hinsetzen — auf dieselbe Taste, mit der man alles andere benutzt.
 *
 * Stuehle sind keine Karten-Objekte, sondern Dekor-Kacheln: sie tragen keinen
 * Inhalt, den man oeffnen koennte, und stehen zu Dutzenden im Stockwerk. Der
 * Sitzplatz wird darum nicht aus `map.objects` gesucht, sondern direkt aus der
 * Dekor-Ebene — reine Geometrie in Tile-Koordinaten, ohne Renderer und ohne
 * Netz.
 */

/**
 * Ist diese Dekor-Kachel ein Stuhl?
 *
 * Stuehle tragen ihre Blickrichtung im Namen (`chair.up`, `chair.left`, …),
 * genau wie die Schreibtische. Das schlichte `chair` ohne Richtung bleibt
 * gueltig — aeltere und von Hand gebaute Karten kennen nur das.
 */
export function istStuhl(tileId: string | undefined): boolean {
  return tileId === 'chair' || (tileId?.startsWith('chair.') ?? false);
}

/** So weit darf der Stuhl entfernt sein, um sich daraufzusetzen (Tiles). */
export const SITZ_REICHWEITE = 1.6;

/** Was die Benutzen-Taste am Stuhl gerade bewirkt. */
export type SitzAktion = 'hinsetzen' | 'aufstehen';

/** Ein Stuhl in Tile-Koordinaten (linke obere Ecke seiner Kachel). */
export interface Sitzplatz {
  x: number;
  y: number;
}

/** Mitte der Stuhlkachel — dorthin rueckt der Fusspunkt beim Hinsetzen. */
export function sitzMitte(platz: Sitzplatz): { x: number; y: number } {
  return { x: platz.x + 0.5, y: platz.y + 0.5 };
}

/**
 * Welcher Stuhl laesst sich von hier aus benutzen?
 *
 * Wie bei den Objekten entscheidet in der Ego-Ansicht das Fadenkreuz (`yaw`
 * gesetzt) und in der Draufsicht die blosse Naehe. Damit gilt fuer Stuhl und
 * Whiteboard dieselbe Regel — und man setzt sich nicht versehentlich auf
 * einen Stuhl im Ruecken.
 */
export function stuhlInReichweite(
  decor: TileLayer,
  self: { x: number; y: number; yaw?: number | null },
): Sitzplatz | null {
  const kandidaten = stuehleInDerNaehe(decor, self.x, self.y);
  if (kandidaten.length === 0) return null;

  if (self.yaw === null || self.yaw === undefined) {
    return kandidaten.reduce((naechster, platz) =>
      abstand(self, platz) < abstand(self, naechster) ? platz : naechster,
    );
  }

  const blick = forwardFromYaw(self.yaw);
  let getroffen: Sitzplatz | null = null;
  let kuerzeste = Number.POSITIVE_INFINITY;
  for (const platz of kandidaten) {
    const entlangStrahl = rayRectDistance(self.x, self.y, blick.x, blick.y, kachel(platz));
    if (entlangStrahl === null || entlangStrahl >= kuerzeste) continue;
    getroffen = platz;
    kuerzeste = entlangStrahl;
  }
  return getroffen;
}

/** Stuhlkacheln in Reichweite — nur das Quadrat um den Spieler wird gelesen. */
function stuehleInDerNaehe(decor: TileLayer, x: number, y: number): Sitzplatz[] {
  const rand = Math.ceil(SITZ_REICHWEITE);
  const treffer: Sitzplatz[] = [];
  for (let ty = Math.floor(y) - rand; ty <= Math.floor(y) + rand; ty += 1) {
    const zeile = decor[ty];
    if (!zeile) continue;
    for (let tx = Math.floor(x) - rand; tx <= Math.floor(x) + rand; tx += 1) {
      if (!istStuhl(zeile[tx])) continue;
      const platz = { x: tx, y: ty };
      if (abstand({ x, y }, platz) <= SITZ_REICHWEITE) treffer.push(platz);
    }
  }
  return treffer;
}

function kachel(platz: Sitzplatz): { x: number; y: number; w: number; h: number } {
  return { x: platz.x, y: platz.y, w: 1, h: 1 };
}

function abstand(self: { x: number; y: number }, platz: Sitzplatz): number {
  return distanceToRect(self.x, self.y, kachel(platz));
}
